'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
  detectiveAlias: string;
  characterType: string;
  gameCategory: string;
  inviteCode: string;
  agreeTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

const characterTypes = [
  { value: 'psychic', label: '🔮 The Fake Psychic — Overconfident & Charming' },
  { value: 'analyst', label: '🔍 The Detail Analyst — Nothing Escapes You' },
  { value: 'sidekick', label: '🤝 The Loyal Sidekick — Team Player First' },
  { value: 'skeptic', label: '🧐 The Skeptic — You Need Proof for Everything' },
  { value: 'superfan', label: '⭐ The Superfan — Knows Every Episode' },
];

const gameCategories = [
  { value: 'trivia', label: '🗂️ Case File Trivia' },
  { value: 'quote', label: '💬 Quote or Not' },
  { value: 'observation', label: '🔮 Psychic Observation' },
  { value: 'pineapple', label: '🍍 Pineapple Hunt' },
  { value: 'speed', label: '🚙 Blueberry Challenge' },
  { value: 'memory', label: '🔍 Crime Scene Memory' },
];

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const supabase = createClient();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupFormData>();
  const password = watch('password');

  useEffect(() => {
    const fetchInviteSetting = async () => {
      try {
        const { data } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'invite_only')
          .single();
        setInviteOnly(data?.value === 'true');
      } catch {
        setInviteOnly(false);
      } finally {
        setCheckingSettings(false);
      }
    };
    fetchInviteSetting();
  }, []);

  const validateInviteCode = async (code: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('invite_codes')
      .select('id, use_count, max_uses, expires_at, is_active')
      .eq('code', code.trim().toUpperCase())
      .eq('is_active', true)
      .single();

    if (error || !data) return false;
    if (data.use_count >= data.max_uses) return false;
    if (data.expires_at && new Date(data.expires_at) < new Date()) return false;
    return true;
  };

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      // Validate invite code if invite-only mode is on
      if (inviteOnly) {
        if (!data.inviteCode?.trim()) {
          toast.error('An invite code is required to sign up');
          setIsLoading(false);
          return;
        }
        const valid = await validateInviteCode(data.inviteCode);
        if (!valid) {
          toast.error('Invalid or expired invite code');
          setIsLoading(false);
          return;
        }
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            display_name: data.displayName,
            detective_alias: data.detectiveAlias,
          }
        }
      });

      if (signUpError) {
        toast.error(signUpError.message || 'Failed to create account');
        return;
      }

      // Increment invite code use count if applicable
      if (inviteOnly && data.inviteCode?.trim()) {
        const normalizedCode = data.inviteCode.trim().toUpperCase();
        const { data: codeRow } = await supabase
          .from('invite_codes')
          .select('id, use_count, max_uses')
          .eq('code', normalizedCode)
          .single();

        if (codeRow) {
          const newCount = (codeRow.use_count || 0) + 1;
          await supabase
            .from('invite_codes')
            .update({
              use_count: newCount,
              is_active: newCount < codeRow.max_uses,
              used_by: authData.user?.id ?? null,
              used_at: new Date().toISOString(),
            })
            .eq('id', codeRow.id);
        }
      }

      toast.success('Account created! Setting up your Detective ID...');
      setTimeout(() => { window.location.href = '/user-profile-creation'; }, 1000);
    } catch (err: any) {
      toast.error(err.message || 'Sign up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1.5">Open Your First Case File</h1>
        <p className="text-sm text-muted-foreground">Create your fan account and get your Detective Agency ID</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="signup-displayname" className="block text-sm font-medium text-foreground mb-1.5">
              Display Name <span className="text-negative">*</span>
            </label>
            <input
              id="signup-displayname"
              type="text"
              placeholder="ShawnFan42"
              {...register('displayName', { required: 'Display name is required', minLength: { value: 3, message: 'At least 3 characters' } })}
              className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            />
            {errors.displayName && <p className="mt-1 text-xs text-negative">{errors.displayName.message}</p>}
          </div>
          <div>
            <label htmlFor="signup-alias" className="block text-sm font-medium text-foreground mb-1.5">
              Detective Alias <span className="text-negative">*</span>
            </label>
            <p className="text-[10px] text-muted-foreground mb-1">Your in-game identity</p>
            <input
              id="signup-alias"
              type="text"
              placeholder="The Pineapple Whisperer"
              {...register('detectiveAlias', { required: 'Detective alias is required' })}
              className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            />
            {errors.detectiveAlias && <p className="mt-1 text-xs text-negative">{errors.detectiveAlias.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-foreground mb-1.5">
            Email Address <span className="text-negative">*</span>
          </label>
          <input
            id="signup-email"
            type="email"
            placeholder="your@email.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^@]+@[^@]+\.[^@]+$/, message: 'Enter a valid email' }
            })}
            className="w-full px-4 py-3 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          />
          {errors.email && <p className="mt-1 text-xs text-negative">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-foreground mb-1.5">
            Password <span className="text-negative">*</span>
          </label>
          <p className="text-[10px] text-muted-foreground mb-1">Minimum 8 characters, include a number</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'At least 8 characters required' },
                pattern: { value: /\d/, message: 'Must contain at least one number' }
              })}
              className="w-full px-4 py-3 pr-11 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-negative">{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-medium text-foreground mb-1.5">
            Confirm Password <span className="text-negative">*</span>
          </label>
          <input
            id="signup-confirm"
            type="password"
            placeholder="••••••••"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: v => v === password || 'Passwords do not match'
            })}
            className="w-full px-4 py-3 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          />
          {errors.confirmPassword && <p className="mt-1 text-xs text-negative">{errors.confirmPassword.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-chartype" className="block text-sm font-medium text-foreground mb-1.5">
            Favorite Character Type
          </label>
          <select
            id="signup-chartype"
            {...register('characterType')}
            className="w-full px-4 py-3 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          >
            <option value="">Select your detective style...</option>
            {characterTypes.map((t) => (
              <option key={`chartype-${t.value}`} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="signup-gamecategory" className="block text-sm font-medium text-foreground mb-1.5">
            Preferred Game Category
          </label>
          <select
            id="signup-gamecategory"
            {...register('gameCategory')}
            className="w-full px-4 py-3 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          >
            <option value="">Choose your specialty...</option>
            {gameCategories.map((g) => (
              <option key={`gamecat-${g.value}`} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>

        {/* Invite Code Field — shown only when invite-only mode is active */}
        {!checkingSettings && inviteOnly && (
          <div>
            <label htmlFor="signup-invite" className="block text-sm font-medium text-foreground mb-1.5">
              Invite Code <span className="text-negative">*</span>
            </label>
            <p className="text-[10px] text-muted-foreground mb-1">This site is currently invite-only</p>
            <div className="relative">
              <Ticket size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="signup-invite"
                type="text"
                placeholder="XXXX-XXXX-XXXX"
                autoCapitalize="characters"
                {...register('inviteCode', {
                  required: inviteOnly ? 'Invite code is required' : false,
                })}
                className="w-full pl-9 pr-4 py-3 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all font-mono tracking-widest uppercase"
              />
            </div>
            {errors.inviteCode && <p className="mt-1 text-xs text-negative">{errors.inviteCode.message}</p>}
          </div>
        )}

        <div className="flex items-start gap-2.5 pt-1">
          <input
            id="agree-terms"
            type="checkbox"
            {...register('agreeTerms', { required: 'You must agree to continue' })}
            className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
          />
          <label htmlFor="agree-terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
            I agree to the{' '}
            <a href="#" className="text-primary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>
            . I understand this is a fan-made platform.
          </label>
        </div>
        {errors.agreeTerms && <p className="text-xs text-negative">{errors.agreeTerms.message}</p>}

        <button
          type="submit"
          disabled={isLoading || checkingSettings}
          className="w-full py-3.5 bg-primary text-primary-foreground text-sm font-bold rounded-xl hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Creating your Detective ID...</span>
            </>
          ) : (
            '🍍 Create Detective Account'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:text-primary/80 transition-colors">
          Sign in
        </button>
      </p>
    </div>
  );
}